import * as inputFileUtils from '../../inputFileUtils';
import { StepAction } from './stepAction';
import { getLogger } from '../../commons/logger';
import { extractElementId, download } from '../../utils';
import { featureFlagsService } from '../../commons/featureFlagsService';
import type { InputFileStep } from 'clickim/src/common/models/inputFileStep';

const logger = getLogger('input-file-step-action');

export class InputFileStepAction extends StepAction<InputFileStep> {
    private uploadFile = (localFileLocation: string) => this.driver.uploadFile(localFileLocation);

    private forceInputToBeVisible(target: ReturnType<InputFileStepAction['getTarget']>, visibleScriptOptions?: { width: string; height: string; left: string; top: string }) {
        logger.info('workaround - stepaction - move element to visible position');

        const getVisibleElementCode = `
            var getLocatedElement = ${this.sessionPlayerInit.codeSnippets.getLocatedElementCode};
            var getVisibleElement = ${inputFileUtils.getVisibleElementScript(visibleScriptOptions)};
            return getVisibleElement.apply(null, arguments);
        `;

        return this.driver.executeJS(getVisibleElementCode, target.locatedElement);
    }

    private async safariPreUploadActions(target: ReturnType<InputFileStepAction['getTarget']>) {
        const options = {
            width: '150px',
            height: '150px',
            left: '10px',
            top: '10px',
        };
        try {
            return await this.forceInputToBeVisible(target, options);
        } catch (err) {
            logger.error('failed to set input file in Safari recovery', { err });
            throw err;
        }
    }

    private async uploadFilesAndForceVisibility(gridLocalFiles: string[], target: ReturnType<InputFileStepAction['getTarget']>) {
        try {
            if (this.driver.isSafari()) {
                await this.safariPreUploadActions(target);
            }
            await this.uploadFiles(gridLocalFiles, target);
        } catch (err) {
            const edgeErrorEditableMessage = 'The element is not editable';
            const edgeErrorFocusableMessage = 'The element is not focusable';
            const safariErrorVisibleMessage = 'An element command could not be completed because the element is not visible on the page.';
            const elementNotInteractable = 'element not interactable';
            const elementNotPointerOrKeyboardInteractable = 'element is not pointer- or keyboard interactable';
            const invalidStateMsg = 'invalid element state: Element is not currently interactable and may not be manipulated';
            const mustBeVisibleMsg = 'Element must not be hidden, disabled or read-only';
            const notReachableByKeyboard = 'is not reachable by keyboard';
            const errorMsg: string = err ? err.message : '';
            // Workaround move element if element is not visible or disabled
            if (
                errorMsg === invalidStateMsg ||
                errorMsg.startsWith(mustBeVisibleMsg) ||
                errorMsg.startsWith(edgeErrorEditableMessage) ||
                errorMsg.startsWith(edgeErrorFocusableMessage) ||
                errorMsg.startsWith(safariErrorVisibleMessage) ||
                errorMsg.includes(notReachableByKeyboard) ||
                errorMsg.includes(elementNotInteractable) ||
                errorMsg.includes(elementNotPointerOrKeyboardInteractable)
            ) {
                await this.forceInputToBeVisible(target);
                await this.uploadFiles(gridLocalFiles, target);
                return;
            }

            logger.error('failed to set input file', { err });
            throw err;
        }
    }

    private async uploadFiles(gridLocalFiles: string[], target: ReturnType<InputFileStepAction['getTarget']>) {
        for (const file of gridLocalFiles) {
            await this.driver.elementIdValue(extractElementId(target.seleniumElement), file);
        }
    }

    override async performAction() {
        const target = this.getTarget();
        const overrideAzureStorageUrl = featureFlagsService.flags.overrideAzureStorageUrl.isEnabled();
        const downloadToBase64 = featureFlagsService.flags.downloadToBase64.isEnabled();
        const { sessionPlayerInit, context, step, stepActionUtils, driver } = this;

        let fileUrls = await sessionPlayerInit.utils.addTokenToFileUrls(
            context.project.id,
            step.fileUrls!,
            stepActionUtils.testimServicesApi,
            overrideAzureStorageUrl,
            logger,
        );

        if (fileUrls.length === 0) {
            throw new Error('No files urls to upload');
        }

        if (downloadToBase64) {
            fileUrls = await Promise.all(fileUrls.map(async ({ name, url }) => {
                const res = await download(url);
                return { name, url: `data:${res.type};base64,${Buffer.from(res.body).toString('base64')}` };
            }));
        }

        if (driver.isSafari() || driver.isFirefox() || driver.isEdgeChromium()) {
            await this.driver.executeJS(`
                const getLocatedElement = ${this.sessionPlayerInit.codeSnippets.getLocatedElementCode};
                const downloadAndUploadFile = ${downloadAndUpload()};
                return downloadAndUploadFile.apply(null, arguments);`, target.locatedElement, fileUrls);
            return;
        }

        const gridLocalFiles = await inputFileUtils.downloadFilesAndUploadToGrid(fileUrls, this.uploadFile) as string[];
        await this.uploadFilesAndForceVisibility(gridLocalFiles, target);
    }
}

function downloadAndUpload() {
    return `async function downloadAndUpload(locatedElement, fileUrls) {
        const fileIsNative = typeof window.File === 'function' && (window.File.toString().indexOf('native code') > -1);
        const File = fileIsNative ? window.File : (function obtainSafeGlobals() {
            const attachTo  = document.body || document.documentElement;
            if (attachTo) {
                let tempIFrame;
                try {
                    tempIFrame = document.createElement('iframe');
                    tempIFrame.style.setProperty('display', 'none');
                    tempIFrame.style.setProperty('pointer-events', 'none');
                    attachTo.appendChild(tempIFrame);
                    const { File } = tempIFrame.contentWindow;
                    return File;
                } finally {
                    if (tempIFrame) {
                        tempIFrame.parentElement.remove(frame);
                    }
                }
            } else {
                return window.File;
            }
        })();
        const element = getLocatedElement(locatedElement);
        if(!element) {
            throw new Error('element not found');
        }

        function getFileBlob(url) {
            return new Promise((resolve, reject) => {
                var xhr = new XMLHttpRequest();
                xhr.open('GET', url, true);
                xhr.responseType = 'blob';

                xhr.onload = function() {
                    if (this.status === 200) {
                        resolve(this.response);
                    } else {
                        reject(new Error('Could not load file, failure status:' + this.status));
                    }
                }
                xhr.onerror = function(e) {
                    reject(new Error("Error " + e.target.status + " occurred while loading the file."));
                };
                xhr.send();
            });

        }

        const fileList = await Promise.all(fileUrls.map(async ({ url, name }) => {
            let blob;
            try {
                const res = await fetch(url)
                blob = await res.blob();
            } catch (err) {
                blob = await getFileBlob(url); // Sometimes the fetch fails, try using XHR as fallback
            }
            return new File([blob], name, { type: blob.type });
        }));

        const dt = new DataTransfer();
        for (const file of fileList) {
            dt.items.add(file);
        }
        element.files = dt.files;

        let changeWasFired = false;
        const changeFiredHandler = (e) => {
            changeWasFired = true;
        };

        element.addEventListener("change", changeFiredHandler, true);
        await Promise.resolve(); // wait microtick
        element.dispatchEvent(new Event("input", { bubbles: true }));
        if (!changeWasFired) {
            element.dispatchEvent(new Event("change", { bubbles: true }));
        }
        element.removeEventListener("change", changeFiredHandler, true);
    }`;
}
